/**
 * The bearer token from /auth/login.
 *
 * Why the app holds one at all
 * ----------------------------
 * The session cookie is HttpOnly and is still what carries the session for
 * `<iframe src>` and download links, which cannot send a header. Every fetch in
 * this app additionally sends the same token as `Authorization: Bearer`, so the
 * browser and a service calling the API with curl take the identical path
 * through the server's guard. One code path gets exercised instead of two, and
 * "works in the UI but 401s from Postman" stops being possible.
 *
 * Where it is kept
 * ----------------
 * sessionStorage, not localStorage: it dies with the tab, so a shared machine
 * does not leave a signed-in console for the next person. It is readable by
 * script either way -- that is the cost of having the token in the body at all,
 * and it is written down in auth_routes.login_route. The cookie remains the
 * safer copy and is not affected.
 *
 * Every access is wrapped: storage throws outright in some privacy modes, and
 * the console should degrade to "cookie only, works fine" rather than fail to
 * render.
 */

const KEY = "ppd_access_token";

/** In-memory copy, so the common path never touches storage. */
let current: string | null = null;

function fromStorage(): string | null {
    try {
        return window.sessionStorage.getItem(KEY);
    } catch {
        return null;
    }
}

export function getToken(): string | null {
    if (current === null) current = fromStorage();
    return current || null;
}

export function setToken(token: string | null): void {
    current = token;
    try {
        if (token) window.sessionStorage.setItem(KEY, token);
        else window.sessionStorage.removeItem(KEY);
    } catch {
        /* Non-persistent session: the in-memory copy still serves this tab. */
    }
}

export const clearToken = (): void => setToken(null);
